/**
 * The formatting rules every overlay shares.
 *
 * Both overlay renderers hash their own output, so byte determinism is not a
 * nicety here: two runs over the same document must produce the same file or
 * the manifest's `sha256` means nothing. Fixed precision, trailing zeros
 * stripped, negative zero normalised, and no locale anywhere.
 *
 * These live in their own module rather than being copied into the second
 * renderer, because two copies of a determinism rule is two rules, and the day
 * they disagree is the day a hash changes for no reason anybody can find.
 */

/** Fixed precision; the same value always renders to the same bytes. */
export function formatNumber(n: number): string {
  if (!Number.isFinite(n)) throw new Error(`non-finite coordinate in overlay: ${n}`);
  let s = n.toFixed(4);
  if (s.includes('.')) s = s.replace(/0+$/, '').replace(/\.$/, '');
  return s === '-0' ? '0' : s;
}

/**
 * XML forbids most C0 control characters outright — there is no escape for them,
 * so a document containing one is not ill-formed in a way a parser can recover
 * from; it simply will not parse. Extracted text comes from a file we did not
 * write, so one can arrive, and the result would be an overlay that opens
 * nowhere with no clue why.
 *
 * They are replaced rather than dropped, so the mark still shows that something
 * was there. U+FFFD is the character that means exactly that.
 */
const XML_FORBIDDEN = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\uFFFE\uFFFF]/g;

export function escapeXml(s: string): string {
  return s
    .replace(XML_FORBIDDEN, '\uFFFD')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/**
 * An SVG elliptical-arc command. Angles are counter-clockwise in the entity's
 * own space, and overlays render in PDF space where y increases downward, so
 * the sweep flag is 1 to keep the drawn direction matching the entity.
 */
export function arcPath(
  cx: number,
  cy: number,
  r: number,
  startDeg: number,
  endDeg: number,
): string {
  const f = formatNumber;
  const rad = (d: number): number => (d * Math.PI) / 180;
  const x0 = cx + r * Math.cos(rad(startDeg));
  const y0 = cy + r * Math.sin(rad(startDeg));
  const x1 = cx + r * Math.cos(rad(endDeg));
  const y1 = cy + r * Math.sin(rad(endDeg));
  const sweep = (((endDeg - startDeg) % 360) + 360) % 360;
  const large = sweep > 180 ? 1 : 0;
  return `M ${f(x0)} ${f(y0)} A ${f(r)} ${f(r)} 0 ${large} 1 ${f(x1)} ${f(y1)}`;
}
